import { Injectable } from '@angular/core';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { select, Store } from '@ngrx/store';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { SelectOptionLoaderI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-loader-i';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Observable, of } from 'rxjs';
import { getLoadSizes } from '../../../selectors/single-catalogs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { UntypedFormGroup } from '@angular/forms';
import { Features } from '@ordersnproducts/features';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { createSelectOptions, getLoadSizeLimits } from '../../../helpers/load-size-helper';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';

@Injectable()
export class SingleLoadSizesCreateLoaderService implements SelectOptionLoaderI {
  protected defaultLoadSize = false;
  constructor(
    private store: Store<OrderBuilderState>,
    private experienceService: ExperiencesService,
    private featureToggle: FeatureToggleService,
  ) {}

  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    const experiences = this.experienceService.commandDcoEnabled();
    const experiencesArkik = this.experienceService.isArkikUlEnabled();
    const isRestrictedVolumeEnabled = this.featureToggle.isFeatureEnabled(Features.RESTRICTED_LOAD_SIZE_AND_VOLUME);
    if ((experiences || experiencesArkik) && isRestrictedVolumeEnabled) {
      return this.commandLoadSizeFunctionality(formGroup);
    } else {
      return this.defaultLoadSizeFunctionality(formGroup);
    }
  }

  /**
   * Loads load size options when command functionality is enabled.
   *
   * @param formGroup - The form group containing the current state of the form.
   * @returns An observable that emits an array of SelectOptionI.
   */
  private commandLoadSizeFunctionality(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getLoadSizes),
      filter((loadSizes) => !!loadSizes),
      withLatestFrom(getLoadSizeLimits(this.store)),
      switchMap(([loadSizes, { minLoadSize, maxLoadSize }]) => {
        if (minLoadSize === null || maxLoadSize === null || minLoadSize === undefined || maxLoadSize === undefined) {
          return this.defaultLoadSizeFunctionality(formGroup);
        }

        const validLoadSizes = loadSizes.filter(
          (loadSize) => loadSize.loadSizeQuantity >= minLoadSize && loadSize.loadSizeQuantity <= maxLoadSize,
        );

        if (validLoadSizes.length > 0) {
          const maxLoadSizeValue = validLoadSizes.reduce(
            (max, current) => (current.loadSizeQuantity > max.loadSizeQuantity ? current : max),
            validLoadSizes[0],
          );
          formGroup.get(SpecFieldNames.LOAD_SIZE).setValue(maxLoadSizeValue.loadSizeCode);

          return of(createSelectOptions(validLoadSizes));
        }

        return of([]);
      }),
      catchError((error) => {
        console.error('Error loading command load sizes (create):', error);
        return of([]);
      }),
    );
  }

  /**
   * Loads default load size options when command functionality is not enabled.
   *
   * @param formGroup - The form group containing the current state of the form.
   * @returns An observable that emits an array of SelectOptionI.
   */
  private defaultLoadSizeFunctionality(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getLoadSizes),
      filter((state) => !!state),
      map((loadSizes) => {
        if (loadSizes.length) {
          if (this.defaultLoadSize) {
            const experiences = this.experienceService.commandDcoEnabled();
            experiences && formGroup.get(SpecFieldNames.LOAD_SIZE).setValue('10yd3');
          }
          return loadSizes.map((loadSize) => {
            return {
              id: loadSize.loadSizeCode,
              name: `${loadSize.loadSizeDesc}`,
            };
          });
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error loading command load sizes (create):', error);
        return of([]);
      }),
    );
  }
}
